import React, { useState, useEffect } from 'react';
import { useUsers } from './UsersContext';
import { T, Th, Tr, ActionBtn, Modal, UserDetailFields, TableCard, TableBar, SearchInput, Toast, RejectModal, PrimaryBtn } from './theme';
import { useRefresh } from './RefreshContext';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const ApproveVideosSection = () => {
  const { getUserName, getUserObj } = useUsers();
  const { refresh, triggerRefresh } = useRefresh();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [rejectModal, setRejectModal] = useState({ show: false, contentId: null });
  const [rejectComment, setRejectComment] = useState('');
  const [search, setSearch] = useState('');
  const [viewUser, setViewUser] = useState(null);
  const reviewerName = `${localStorage.getItem('vrvName') || ''} ${localStorage.getItem('vrvSurname') || ''}`.trim() || localStorage.getItem('vrvEmail') || '';

  const fetchPendingVideos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/vrv-youtubecontent-getAll`, { method: 'GET' });
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setVideos((parsed.data || []).filter(v => v.status === 'PENDING').sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchPendingVideos(); }, [refresh]); // eslint-disable-line

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const handleAction = async (content_id, action, comments = '') => {
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE}/vrv-youtubecontent-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ content_id, action, reviewedBy: reviewerName, reviewedAt: new Date().toISOString(), reviewComments: action === 'APPROVE' ? 'APPROVED' : comments }),
      });
      if (res.ok) { showMsg(`Video ${action.toLowerCase()}d successfully!`, 'success'); fetchPendingVideos(); triggerRefresh(); }
      else showMsg('Action failed', 'error');
    } catch { showMsg('Error connecting to server', 'error'); }
  };

  const filteredVideos = videos.filter(v => search === '' || Object.values(v).some(val => String(val).toLowerCase().includes(search.toLowerCase())));

  if (loading) return <p className="text-center py-8 text-sm" style={{ color: T.muted }}>Loading...</p>;
  if (videos.length === 0) return <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No pending videos to approve.</p>;

  return (
    <div>
      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>

      {/* Desktop Table */}
      <div className="desktop-table">
        <TableCard>
          <TableBar>
            <p className="text-xs font-bold" style={{ color: T.gold }}>{filteredVideos.length} video(s) pending approval</p>
          </TableBar>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr><Th>Title</Th><Th>Category</Th><Th>Created By</Th><Th>URL</Th><Th>Date</Th><Th center>Actions</Th></tr></thead>
              <tbody>
                {filteredVideos.map((video, i) => (
                  <Tr key={video.content_id || i}>
                    <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{video.title || '—'}</td>
                    <td className="px-3 py-2.5" style={{ color: T.muted }}>{video.category}</td>
                    <td className="px-3 py-2.5">{video.createdBy ? <button onClick={() => { const u = getUserObj(video.createdBy); if (u) setViewUser(u); }} className="font-bold hover:underline text-xs" style={{ color: T.gold }}>{getUserName(video.createdBy) || '...'}</button> : '—'}</td>
                    <td className="px-3 py-2.5"><a href={video.youtube_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate block max-w-[150px]" style={{ color: T.dark }}>{video.youtube_url}</a></td>
                    <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{video.createdAt ? new Date(video.createdAt).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2.5 text-center">
                      <div className="flex gap-1.5 justify-center">
                        <ActionBtn onClick={() => handleAction(video.content_id, 'APPROVE')} color="green">Approve</ActionBtn>
                        <ActionBtn onClick={() => setRejectModal({ show: true, contentId: video.content_id })} color="red">Reject</ActionBtn>
                      </div>
                    </td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        </TableCard>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        <p className="text-xs font-bold mb-3" style={{ color: T.gold }}>{filteredVideos.length} video(s) pending approval</p>
        {filteredVideos.map((video, i) => (
          <div key={video.content_id || i} className="vrv-mobile-card">
            <p className="font-bold text-sm truncate mb-1" style={{ color: T.navy }}>{video.title || '—'}</p>
            <div className="grid grid-cols-2 gap-1 text-xs mb-3">
              <div><span style={{ color: T.muted }}>Category: </span>{video.category}</div>
              <div><span style={{ color: T.muted }}>Date: </span>{video.createdAt ? new Date(video.createdAt).toLocaleDateString() : '—'}</div>
              <div><span style={{ color: T.muted }}>By: </span>{video.createdBy ? <button onClick={() => { const u = getUserObj(video.createdBy); if (u) setViewUser(u); }} className="font-bold" style={{ color: T.gold }}>{getUserName(video.createdBy) || '...'}</button> : '—'}</div>
            </div>
            <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <ActionBtn onClick={() => handleAction(video.content_id, 'APPROVE')} color="green" small>Approve</ActionBtn>
              <ActionBtn onClick={() => setRejectModal({ show: true, contentId: video.content_id })} color="red" small>Reject</ActionBtn>
            </div>
          </div>
        ))}
      </div>

      {viewUser && <Modal onClose={() => setViewUser(null)} title="User Details" subtitle="Member Info" wide><UserDetailFields user={viewUser} /><div className="mt-6"><PrimaryBtn onClick={() => setViewUser(null)}>Close</PrimaryBtn></div></Modal>}
      {rejectModal.show && <RejectModal value={rejectComment} onChange={e => setRejectComment(e.target.value)} title="Reject Video" onCancel={() => { setRejectModal({ show: false, contentId: null }); setRejectComment(''); }} onConfirm={() => { handleAction(rejectModal.contentId, 'REJECT', rejectComment); setRejectModal({ show: false, contentId: null }); setRejectComment(''); }} />}
      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default ApproveVideosSection;
